import SocketCommand from '@/api/SocketCommand.js'
import {clone, sortBy} from '@/lib/helper/helper.js'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum.js'

/**
 * Handles incoming order update socket commands.
 *
 * @param {string} command - The socket command name.
 * @param {Object} data - The payload containing action and order_id.
 */
export const handleOrderUpdate = (command, data) => {
  if (!data?.order_id || !data?.action) return
  if (data.action === 'update' && userCanAccessOrderWithSourceType(data?.source_type)) {
    // if (Object.hasOwn(data, 'is_generating_receipt')) updateDailyOrderProperty(data?.order_id, 'IsGeneratingReceipt', data.is_generating_receipt)
    getDailyOrder(data.order_id, data.action)
  } else if (data.action === 'create' && userCanAccessOrderWithSourceType(data?.source_type)) {
    getDailyOrder(data.order_id, data.action)
  } else if (data.action === 'delete') {
    removeDailyOrder(data.order_id)
  }
}

/**
 * Requests the details of a specific daily order via socket command.
 *
 * @param {number|string} id - The ID of the order to retrieve.
 * @param {string} [action='update'] - The action (update or create) of the order to retrieve.
 */
export const getDailyOrder = (id, action = 'update') => {
  if (!id) return
  window.callAS(SocketCommand.Order.DailyGet, {order_id: id, action: action || null})
}

/**
 * Determines whether to add or update an order in the daily list based on the action.
 *
 * @param {Object} item - The order object.
 * @param {string} action - The action to perform ('create' or 'update').
 */
export const addOrUpdateDailyOrder = (item, action) => {
  if (!item || !action) return

  if (action === 'update') updateDailyOrder(item)
  if (action === 'create') addDailyOrder(item)
}


/**
 * Updates an existing order in the daily list.
 *
 * @param {Object} item - The order object containing updated data.
 */
export const updateDailyOrder = (item) => {
  if (!item) return
  const orders = getOrders()
  const index = getOrderIndex(orders, item.Id)

  if (index > -1) {
    orders[index] = item
    if (item.IsScheduled && !item.ScheduledToday && ![OrderStatusEnum.Received, OrderStatusEnum.Pending].includes(item.StatusId)) {
      removeDailyOrder([item.Id])
      return
    }

    updateDailyOrderList(orders)
  }
}

/**
 * Updates a specific property of an order in the daily list.
 *
 * @param {number|string} id - The ID of the order to update.
 * @param {string} property - The property name to update.
 * @param {*} value - The new value for the property.
 */
export const updateDailyOrderProperty = (id, property, value) => {
  if (!id || !property) return
  const orders = getOrders()
  const index = getOrderIndex(orders, id)

  if (index > -1) {
    orders[index][property] = value
    updateDailyOrderList(orders)
  }
}

/**
 * Adds a new order to the daily list and sorts the list.
 *
 * @param {Object} item - The order object to be added.
 */
export const addDailyOrder = (item) => {
  if (!item) return
  let orders = getOrders()
  const index = getOrderIndex(orders, item.Id)

  if (index < 0) {
    orders.unshift(item)
    orders = sortBy(orders, 'Timestamp', 'desc')
    updateDailyOrderList(orders)
  }
}

/**
 * Removes orders from the daily order list by their IDs.
 *
 * @param {Array<number|string>} id - An array of order IDs to be removed.
 */
export const removeDailyOrder = (id) => {
  if (!Array.isArray(id) || !id?.length) return
  const orders = getOrders()

  id.forEach(orderId => {
    const index = getOrderIndex(orders, orderId)
    if (index > -1) orders.splice(index, 1)
  })

  updateDailyOrderList(orders)
}

/**
 * Updates the daily order list in the data store and emits a socket command event.
 *
 * @param {Array<Object>} orders - The updated list of order objects.
 */
const updateDailyOrderList = (orders) => {
  if (!Array.isArray(orders)) return

  const vm = window?.Vue
  vm.$set(vm.$DataStore.ordersDataToday, 'items', orders)
  vm.$bus.$emit(SocketCommand.Order.DailyList, orders)
}

/**
 * Retrieves the current list of daily orders from the data store.
 *
 * @returns {Array<Object>} A cloned array of order objects.
 */
const getOrders = () => {
  const vm = window?.Vue
  return clone(vm.$DataStore.ordersDataToday.items || [])
}

/**
 * Finds the index of an order within an array by its ID.
 *
 * @param {Array<Object>} orders - The array of order objects to search.
 * @param {number|string} orderId - The ID of the order to find.
 * @returns {number} The index of the order if found, otherwise -1.
 */
const getOrderIndex = (orders, orderId) => {
  return orders?.findIndex(order => order.Id === orderId) ?? -1
}

const userCanAccessOrderWithSourceType = (orderSourceTypeId) => {
  if (!orderSourceTypeId) return false

  const vm = window.Vue
  const userAllowedSourceTypeIds = vm.posUser?.SourceTypeIds
  return userAllowedSourceTypeIds ? userAllowedSourceTypeIds.includes(orderSourceTypeId) : true
}

export const autoDisableEnableItem = (item, timeout = 3500) => {
  if (!item) return
  const vm = window?.Vue

  item.disabled = true
  vm.$forceUpdate()

  clearTimeout(item.timeout)
  item.timeout = setTimeout(() => {
    item.disabled = false
    vm.$forceUpdate()
  }, timeout)
}
